import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Edit, Info, Send, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import {
  useFirmNotificationPoliciesQuery,
  type FirmNotificationPolicy,
  type NotificationSeverity,
} from '@/lib/api/firm-notifications';
import { cn } from '@/lib/utils/cn';

/**
 * T-0116 / E09-F03 — Notification email template modal
 * (`data-open-email-templates`).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-d3.js`
 * → `window.Modals.openEmailTemplate`. Three-pane layout: template list
 * on the left + rendered preview in the middle + dispatch context on
 * the right.
 *
 * Behavior:
 *   - Backed by the firm-notification-policies surface (T-0112 /
 *     `useFirmNotificationPoliciesQuery`). Each policy's event_type
 *     becomes a template row; severity + category + audience scopes
 *     drive the rendered preview metadata.
 *   - The actual template body / subject / variables are static in v1.
 *     The notification-fanout taxonomy (packages/notification-fanout/
 *     src/taxonomy.ts) carries the canonical `event_type`s but not the
 *     rendered subject/body — those live in the (future) email-template
 *     storage surface (no T-0079 read endpoint exposes them today).
 *     Flagged as a backend gap; the modal renders a synthesized
 *     preview using the policy's category + severity so the inspector
 *     still surfaces the per-event shape.
 *   - "Edit copy" + "Send test" are admin actions that need backend
 *     wire-up; v1 renders them as disabled CTAs with title-attribute
 *     callouts.
 *
 * Auth: firm_admin / engagement_lead only (matches the firm-notification
 * surfaces this modal launches from).
 */

const FIRM_DISPLAY_NAME = 'Caliber';
const FIRM_EMAIL = 'notifications@avero.example';

interface SyntheticPreview {
  subject: string;
  summary: string;
}

function synthesize(policy: FirmNotificationPolicy): SyntheticPreview {
  // v1 synthesizes the subject + summary from the policy label + category.
  // Replace with a real read once the email-template storage endpoint
  // lands. Keeping the shape stable so the swap is mechanical.
  const subjectMap: Partial<Record<string, string>> = {
    'pulse.published': '{{engagement_name}} — Contract pulse published',
    'snapshot.captured': 'New snapshot available — {{engagement_name}}',
    'deliverable.overdue': 'Reminder: deliverable past due on {{engagement_name}}',
    'deliverable.rejected': 'Deliverable rejected on {{engagement_name}}',
    'deviation.logged': 'Action required — deviation logged on {{engagement_name}}',
    'finding.severity_crossed': '{{engagement_name}} — finding severity crossed',
    'score.threshold_crossed': '{{engagement_name}} — readiness band crossed',
    'override.captured': 'Override captured on {{engagement_name}}',
    'register.changed': 'Register update on {{engagement_name}}',
    'reeval.flagged': '{{engagement_name}} — AI re-evaluation flagged',
    'routing.requested': 'Routing requested on {{engagement_name}}',
    'routing.decided': 'Routing decided on {{engagement_name}}',
    'proposals.ready': 'AI proposals ready — {{engagement_name}}',
    'synthesis.published': '{{engagement_name}} — Health synthesis published',
    'readiness.report_published': '{{engagement_name}} — Readiness report published',
    'domain.rating_changed': '{{engagement_name}} — domain rating changed',
    'document.parsed': 'Document parsed on {{engagement_name}}',
    'document.indexed': 'Document indexed on {{engagement_name}}',
  };
  return {
    subject: subjectMap[policy.event_type] ?? `${policy.label} — {{engagement_name}}`,
    summary: `${policy.label}. Category: ${policy.category}. Severity: ${policy.severity}.`,
  };
}

const TEMPLATE_VARIABLES: ReadonlyArray<string> = [
  'recipient',
  'recipient_email',
  'recipient_first_name',
  'recipient_role',
  'engagement_name',
  'engagement_client',
  'event_id',
  'event_timestamp',
  'deep_link',
  'prefs_link',
  'unsubscribe',
];

export interface EmailTemplateModalProps {
  /** Initial template to focus, by event_type. Falls back to the first row. */
  initialEventType?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmailTemplateModal({
  initialEventType,
  open,
  onOpenChange,
}: EmailTemplateModalProps) {
  const query = useFirmNotificationPoliciesQuery();
  const policies = query.data?.policies ?? [];

  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!open) return;
    if (initialEventType) {
      setActiveKey(initialEventType);
      return;
    }
    if (policies[0]) setActiveKey(policies[0].event_type);
  }, [open, initialEventType, policies]);

  const active = policies.find((p) => p.event_type === activeKey) ?? policies[0] ?? null;
  const preview = active ? synthesize(active) : null;

  const titleId = 'email-template-title';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1180px,96vw)] max-h-[92vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <Header titleId={titleId} active={active} />
          <div className="grid min-h-0 grid-cols-1 overflow-hidden lg:grid-cols-[260px_1fr_300px]">
            <TemplateList
              policies={policies}
              loading={query.isLoading}
              activeKey={activeKey}
              onPick={setActiveKey}
            />
            <PreviewPane active={active} preview={preview} />
            <SidePanel active={active} />
          </div>
          <Footer />
          <Dialog.Close
            aria-label="Close email template inspector"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Header({ titleId, active }: { titleId: string; active: FirmNotificationPolicy | null }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-6 py-4 pr-14">
      <div className="min-w-0">
        <p className="text-meta uppercase tracking-wide text-ink-500">
          Notification email template
        </p>
        <Dialog.Title id={titleId} className="mt-1 truncate text-h2 font-semibold text-ink-900">
          {active?.label ?? 'Email templates'}
        </Dialog.Title>
        {active ? (
          <p className="mt-1 text-meta text-ink-500">
            Category <strong className="text-ink-700">{active.category}</strong> &middot; Severity{' '}
            <SeverityPill severity={active.severity} /> &middot; Avero brand v3.2
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled
          title="Editing an email's wording is not available yet"
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5',
            'text-meta font-medium text-ink-700 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Edit className="h-3 w-3" aria-hidden="true" />
          Edit copy
        </button>
        <button
          type="button"
          disabled
          title="Sending a test email is not available yet"
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2.5',
            'text-meta font-medium text-ink-700 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Send className="h-3 w-3" aria-hidden="true" />
          Send test
        </button>
      </div>
    </div>
  );
}

function TemplateList({
  policies,
  loading,
  activeKey,
  onPick,
}: {
  policies: ReadonlyArray<FirmNotificationPolicy>;
  loading: boolean;
  activeKey: string | null;
  onPick: (key: string) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 border-b border-ink-100 bg-paper-50 p-4 text-meta text-ink-500 lg:border-b-0 lg:border-r">
        <Spinner /> Loading templates…
      </div>
    );
  }
  const grouped = groupByCategory(policies);
  return (
    <aside className="overflow-y-auto border-b border-ink-100 bg-paper-50 lg:border-b-0 lg:border-r">
      <div className="px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-700">
          Templates &middot; {policies.length}
        </p>
        <p className="mt-1 text-meta leading-[18px] text-ink-500">
          Per event_type, drawn from the firm notification taxonomy.
        </p>
      </div>
      {grouped.map(([category, rows]) => (
        <div key={category} className="border-t border-ink-100">
          <p className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-ink-500">
            {category}
          </p>
          {rows.map((p) => {
            const active = p.event_type === activeKey;
            return (
              <button
                key={p.event_type}
                type="button"
                onClick={() => onPick(p.event_type)}
                className={cn(
                  'block w-full border-l-2 px-4 py-2.5 text-left',
                  'transition-colors hover:bg-paper-100',
                  active ? 'border-l-avero-orange bg-paper-0' : 'border-l-transparent',
                )}
              >
                <span className="block text-body-sm font-medium text-ink-900">{p.label}</span>
                <span className="mt-0.5 block font-mono text-[10px] text-ink-500">
                  {p.event_type}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </aside>
  );
}

function PreviewPane({
  active,
  preview,
}: {
  active: FirmNotificationPolicy | null;
  preview: SyntheticPreview | null;
}) {
  return (
    <section className="overflow-y-auto bg-paper-100 px-6 py-5">
      {active && preview ? (
        <div className="mx-auto max-w-[640px] overflow-hidden rounded-card border border-ink-100 bg-paper-0 shadow-sm">
          <div className="h-1 bg-avero-orange" aria-hidden="true" />
          <div className="border-b border-ink-100 px-5 py-3 text-meta">
            <Row k="From" v={`${FIRM_DISPLAY_NAME} <${FIRM_EMAIL}>`} />
            <Row
              k="To"
              v={
                <span>
                  <span className="text-ink-700">{'{{recipient}}'}</span>{' '}
                  <span className="font-mono text-ink-500">{'<{{recipient_email}}>'}</span>
                </span>
              }
            />
            <Row k="Subject" v={preview.subject} bold />
          </div>
          <div className="px-5 py-5 text-body-sm leading-relaxed text-ink-700">
            <h2 className="font-serif text-h2 font-semibold text-ink-900">{active.label}</h2>
            <p className="mt-3">Hi {'{{recipient_first_name}}'},</p>
            <p className="mt-2">{preview.summary}</p>
            <div className="mt-3 rounded-card border border-ink-100 bg-paper-50 p-3 text-meta">
              <KV k="Engagement" v="{{engagement_client}} · {{engagement_name}}" />
              <KV k="Event ID" v="{{event_id}}" mono />
              <KV k="Triggered" v="{{event_timestamp}}" mono />
              <KV k="Severity" v={<SeverityPill severity={active.severity} />} />
            </div>
            <a
              className="mt-4 inline-flex h-9 items-center rounded-card bg-avero-orange px-3 text-body-sm font-medium text-paper-0 no-underline"
              href="#preview"
            >
              Open in {FIRM_DISPLAY_NAME}
            </a>
            <p className="mt-3 text-meta text-ink-500">
              If you can&apos;t click the button, paste this link into your browser:
              <br />
              <span className="font-mono text-[11px] text-ink-700">{'{{deep_link}}'}</span>
            </p>
          </div>
          <div className="border-t border-ink-100 bg-paper-50 px-5 py-3 text-meta text-ink-500">
            {FIRM_DISPLAY_NAME} is sending you this notification because you&apos;re a member of{' '}
            <span className="text-ink-700">{'{{engagement_client}}'}</span> (
            <span className="text-ink-700">{'{{recipient_role}}'}</span>).
            <br />
            <a href="#prefs" className="text-avero-orange">
              Manage notification preferences
            </a>{' '}
            &middot;{' '}
            <a href="#unsub" className="text-avero-orange">
              Unsubscribe from this engagement
            </a>
            <br />
            This message and any attachments are confidential. Report deliverability issues to{' '}
            <span className="font-mono">ops@avero.example</span>.
          </div>
        </div>
      ) : (
        <p className="text-meta text-ink-500">Pick a template on the left to preview.</p>
      )}
    </section>
  );
}

function SidePanel({ active }: { active: FirmNotificationPolicy | null }) {
  if (!active)
    return <aside className="border-t border-ink-100 bg-paper-50 p-5 lg:border-l lg:border-t-0" />;
  const channels = channelLabels(active);
  return (
    <aside className="overflow-y-auto border-t border-ink-100 bg-paper-50 p-5 lg:border-l lg:border-t-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-700">
        Routing &amp; policy
      </p>
      <KV k="event_type" v={<span className="font-mono">{active.event_type}</span>} />
      <KV k="severity" v={<SeverityPill severity={active.severity} />} />
      <KV k="category" v={active.category} />
      <KV k="channels" v={channels.join(', ') || '—'} />
      <KV k="audience" v={active.audience_scopes.join(', ')} />

      <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-ink-700">
        Variables in template
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {TEMPLATE_VARIABLES.map((v) => (
          <span
            key={v}
            className="rounded-chip bg-paper-100 px-1.5 py-0.5 font-mono text-[10px] text-ink-700"
          >
            {`{{${v}}}`}
          </span>
        ))}
      </div>

      <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-ink-700">
        Retry policy
      </p>
      <p className="mt-1 text-meta leading-[18px] text-ink-500">
        Up to 3 retries with exponential backoff (15s &middot; 60s &middot; 5min). Failed deliveries
        still write the notification to the user&apos;s in-app inbox per the cross-cutting standard.
      </p>
    </aside>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Info className="h-3 w-3" aria-hidden="true" />
        The preview is built from the notification policy&apos;s subject and summary; each email
        uses the same wording.
      </div>
      <Dialog.Close
        className={cn(
          'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
          'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
        )}
      >
        Close
      </Dialog.Close>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: React.ReactNode; bold?: boolean }) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-16 flex-none text-ink-500">{k}</span>
      <span className={cn('flex-1 truncate text-ink-700', bold && 'font-semibold text-ink-900')}>
        {v}
      </span>
    </div>
  );
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1 text-meta">
      <span className="w-24 flex-none text-ink-500">{k}</span>
      <span className={cn('flex-1 text-ink-700', mono && 'font-mono')}>{v}</span>
    </div>
  );
}

function SeverityPill({ severity }: { severity: NotificationSeverity }) {
  const cls: Record<NotificationSeverity, string> = {
    info: 'bg-ink-100 text-ink-700',
    warning: 'bg-posture-at-risk-tint text-posture-at-risk',
    high: 'bg-accent-amber-100 text-accent-amber-700',
    critical: 'bg-posture-critical-tint text-posture-critical',
  };
  return (
    <span
      className={cn(
        'inline-flex h-[18px] items-center rounded-chip px-2 text-[10px] font-semibold uppercase tracking-wider',
        cls[severity],
      )}
    >
      {severity}
    </span>
  );
}

function channelLabels(p: FirmNotificationPolicy): string[] {
  const out: string[] = [];
  if (p.in_app) out.push('in-app');
  if (p.email) out.push('email');
  if (p.digest) out.push('digest');
  return out;
}

function groupByCategory(
  rows: ReadonlyArray<FirmNotificationPolicy>,
): Array<[string, FirmNotificationPolicy[]]> {
  const map = new Map<string, FirmNotificationPolicy[]>();
  for (const r of rows) {
    const list = map.get(r.category) ?? [];
    list.push(r);
    map.set(r.category, list);
  }
  return [...map.entries()];
}
